import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Loader, Text } from '@aws-amplify/ui-react';
import { API } from 'aws-amplify';
import { FaArrowUp, FaTimes, FaMusic, FaList, FaSearch, FaLanguage, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import "../js/loader.js";
import "../js/appConstants.js";

function FileList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sortBySongNumber, setSortBySongNumber] = useState(false);
  const [sortDirection, setSortDirection] = useState('asc');
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileSearch, setFileSearch] = useState('');
  const [songSearch, setSongSearch] = useState('');
  const [voiceSearchMatches, setVoiceSearchMatches] = useState(null);
  const [ragaQuery, setRagaQuery] = useState('');
  const [ragaSearchData, setRagaSearchData] = useState(null);
  const [ragaSearchLoading, setRagaSearchLoading] = useState(false);
  const [ragaSearchError, setRagaSearchError] = useState('');
  const [ragaFolderFilter, setRagaFolderFilter] = useState('ALL');
  const [ragaLanguageFilter, setRagaLanguageFilter] = useState('ALL');
  const [mainResultsCollapsed, setMainResultsCollapsed] = useState(false);
  const [languageAccordionOpen, setLanguageAccordionOpen] = useState(false);
  const [searchAccordionOpen, setSearchAccordionOpen] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [playlistMenuOpen, setPlaylistMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const formatFileName = (fileName) =>
    fileName.replace(/^\d+[\s_-]*/, '').replace(/\.txt$/, '');
  window.formatFileName = formatFileName;

  useEffect(() => {
    const handleLoad = () => window.clickLog('load', 'homepage');
    if (document.readyState === 'complete') handleLoad();
    else window.addEventListener('load', handleLoad);
    return () => window.removeEventListener('load', handleLoad);
  }, []);

  useEffect(() => { fetchFiles(); }, []);

  useEffect(() => {
    if (location.state?.backFromFile) {
      const { folder, language, fileName } = location.state;
      setSelectedFolder(folder);
      setSelectedLanguage(language);
      setSongSearch(fileName.replace(/^\d+[\s_-]*/, '').replace(/\.txt$/, ''));
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (files.length > 0) {
      const uniqueFolders = [...new Set(files.map(f => f.folder))];
      setFolders(uniqueFolders);
      const sanskritFolders = [...new Set(files.filter(f => f.language === 'SAN').map(f => f.folder))];
      if (!selectedFolder && uniqueFolders.length > 0)
        setSelectedFolder(sanskritFolders.length > 0 ? sanskritFolders[0] : uniqueFolders[0]);
    }
  }, [files, selectedFolder]);

  useEffect(() => {
    if (selectedFolder && files.length > 0) {
      const folderFiles = files.filter(f => f.folder === selectedFolder);
      const uniqueLanguages = [...new Set(folderFiles.map(f => f.language))];
      setLanguages(uniqueLanguages);
      const hasSanskrit = uniqueLanguages.includes('SAN');
      if ((!selectedLanguage || !uniqueLanguages.includes(selectedLanguage)) && uniqueLanguages.length > 0)
        setSelectedLanguage(hasSanskrit ? 'SAN' : uniqueLanguages[0]);
    }
  }, [selectedFolder, files]);

  const fetchFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await API.get('NamapagesAPI', 'files', { headers: { 'Accept': 'application/json' } });
      if (response && (Array.isArray(response) || response.files)) {
        const filesData = response.files || response;
        if (response.playlists && Array.isArray(response.playlists)) setPlaylists(response.playlists);
        setFiles(filesData);
      } else {
        setError('Received invalid data format from server.');
      }
    } catch (err) {
      setError('Please come back later or try again after some time. नाम परते साधन नाहीं');
    } finally {
      setLoading(false);
    }
  };

  const searchByRaga = useCallback(async (requestedRaga, requestedSongText) => {
    const raga = (requestedRaga || '').trim();
    if (raga.length <= 3) return;
    setRagaSearchLoading(true);
    setRagaSearchError('');
    try {
      const response = await fetch('https://api.ml.namapages.com/v2/raga-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ raga, song_text: (requestedSongText || '').trim() || undefined, limit: 1000 })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Raga search is unavailable');
      setRagaQuery(raga);
      setRagaSearchData(data);
      setRagaFolderFilter('ALL');
      setRagaLanguageFilter('ALL');
      if (data.resolved_raga?.raga_key && data.total_results > 0) {
        window.clickLog(
          `raga_search_results - query=${raga}; raga=${data.resolved_raga.raga_key}; results=${data.total_results}`,
          'homepage'
        );
      }
    } catch (err) {
      setRagaSearchData(null);
      setRagaSearchError(err.message || 'Raga search is unavailable');
    } finally {
      setRagaSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const raga = ragaQuery.trim();
    if (raga.length <= 3) {
      if (!raga) {
        setRagaSearchData(null);
        setRagaSearchError('');
        setRagaFolderFilter('ALL');
        setRagaLanguageFilter('ALL');
      }
      return undefined;
    }

    const debounceTimer = setTimeout(() => searchByRaga(raga, songSearch), 350);
    return () => clearTimeout(debounceTimer);
  }, [ragaQuery, songSearch, searchByRaga]);

  useEffect(() => {
    setMainResultsCollapsed(Boolean(
      ragaSearchData?.resolved_raga?.raga_key && ragaSearchData.total_results > 0
    ));
  }, [ragaSearchData]);

  const getFilteredFiles = () => {
    if (!selectedFolder || !selectedLanguage) return [];
    const filtered = files.filter(f => f.folder === selectedFolder && f.language === selectedLanguage);
    filtered.sort((a, b) => formatFileName(a.file_name).localeCompare(formatFileName(b.file_name)));
    return filtered;
  };

  const searchSongFiles = () => {
    if (!songSearch.trim()) return [];
    const searchTerm = songSearch.toLowerCase();
    return files.filter(file => {
      const songNo = file.song_no ? file.song_no.toString() : '';
      const songNoMatch = songNo === searchTerm || songNo.startsWith(searchTerm);
      const fileName = formatFileName(file.file_name || file.name || '').toLowerCase();
      const fileNameMatch = fileName.includes(searchTerm);
      const isNumericSearch = /^\d+$/.test(searchTerm);
      const fileNamePrefix = file.file_name ? file.file_name.match(/^(\d+)/)?.[1] : '';
      const prefixMatch = isNumericSearch && fileNamePrefix && (fileNamePrefix === searchTerm || fileNamePrefix.startsWith(searchTerm));
      return songNoMatch || fileNameMatch || prefixMatch;
    });
  };

  const applySongSearch = (searchValue) => {
    setSongSearch(searchValue);
    if (!searchValue.trim()) return;
    const searchTerm = searchValue.toLowerCase();
    const foundFiles = files.filter(file => {
      const songNo = file.song_no ? file.song_no.toString() : '';
      const songNoMatch = songNo === searchTerm || songNo.startsWith(searchTerm);
      const fileName = formatFileName(file.file_name || file.name || '').toLowerCase();
      const fileNameMatch = fileName.includes(searchTerm);
      const isNumericSearch = /^\d+$/.test(searchTerm);
      const fileNamePrefix = file.file_name ? file.file_name.match(/^(\d+)/)?.[1] : '';
      const prefixMatch = isNumericSearch && fileNamePrefix && (fileNamePrefix === searchTerm || fileNamePrefix.startsWith(searchTerm));
      return songNoMatch || fileNameMatch || prefixMatch;
    });
    if (foundFiles.length > 0) setSelectedFolder(foundFiles[0].folder);
  };

  const performVoiceSearch = async (transcript) => {
    applySongSearch(transcript);
    setVoiceSearchMatches(null);
    try {
      const response = await fetch('https://api.ml.namapages.com/v2/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: transcript, language: selectedLanguage || 'ENG', limit: 50 })
      });
      if (!response.ok) return;
      const data = await response.json();
      const songs = [
        ...(data.main_results?.songs || []),
        ...(data.similar_results?.songs || []),
        ...(data.results || [])
      ];
      const normalise = (v) => formatFileName(v || '').replace(/\.txt$/i, '').trim().toLowerCase();
      const contentMatches = songs.map(song => files.find(file =>
        file.folder === song.folder &&
        file.language === song.language &&
        normalise(file.file_name || file.name) === normalise(song.file_name || song.fileName || song.name || song.title)
      )).filter(Boolean);
      const uniqueMatches = [...new Map(contentMatches.map(f => [f.file_path || `${f.folder}/${f.language}/${f.file_name}`, f])).values()];
      if (uniqueMatches.length > 0) {
        setVoiceSearchMatches(uniqueMatches);
        setSelectedFolder(uniqueMatches[0].folder);
      }
    } catch (err) {
      console.warn('Voice content search fallback to local title search:', err);
    }
  };

  const [folderSearch, setFolderSearch] = useState('');
  const [languageSearch, setLanguageSearch] = useState('');

  const formatFolderName = (folder) =>
    folder.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const filteredFolders = folderSearch
    ? folders.filter(f => f.toLowerCase().replace(/_/g, ' ').includes(folderSearch.toLowerCase()))
    : folders;

  const languageCodeMap = {
    'ENG': 'English', 'TAM': 'Tamil', 'IAST': 'English (IAST)',
    'SAN': 'Sanskrit', 'MIX': 'Mixed', 'DEV': 'Devanagari', 'ROM': 'Roman'
  };

  const getLanguageDisplay = (lang) => {
    if (languageCodeMap[lang]) return `${languageCodeMap[lang]} — ${lang}`;
    for (const [code, name] of Object.entries(languageCodeMap))
      if (lang.includes(code)) return `${name} — ${code}`;
    return lang;
  };

  const filteredLanguages = languages.filter(l =>
    l.toLowerCase().includes(languageSearch.toLowerCase()) ||
    getLanguageDisplay(l).toLowerCase().includes(languageSearch.toLowerCase())
  );

  const handleFolderChange = (e) => {
    const newFolder = e.target.value;
    setSelectedFolder(newFolder);
    const folderFiles = files.filter(f => f.folder === newFolder);
    const availableLanguages = [...new Set(folderFiles.map(f => f.language))];
    const hasSanskrit = availableLanguages.includes('SAN');
    if (availableLanguages.length > 0)
      setSelectedLanguage(hasSanskrit ? 'SAN' : availableLanguages[0]);
    else setSelectedLanguage('');
  };

  const handleLanguageChange = (e) => setSelectedLanguage(e.target.value);

  const togglePlaylistMenu = () => setPlaylistMenuOpen(!playlistMenuOpen);

  const handlePlaylistSelect = (playlist) => {
    setPlaylistMenuOpen(false);
    window.clickLog('Selected playlist:', playlist, 'homepage');
    navigate(`/playlist/${playlist.id}`, { state: { playlist } });
  };

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 200);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const highlightMatchedText = (text, query) => {
    if (!query) return text;
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const startIndex = lowerText.indexOf(lowerQuery);
    if (startIndex === -1) return text;
    const endIndex = startIndex + lowerQuery.length;
    return (
      <>
        {text.substring(0, startIndex)}
        <span className="np-highlight">{text.substring(startIndex, endIndex)}</span>
        {text.substring(endIndex)}
      </>
    );
  };

  const getLanguageSongCounts = () => {
    if (!selectedFolder) return [];
    const counts = {};
    files.filter(f => f.folder === selectedFolder).forEach(f => {
      counts[f.language] = (counts[f.language] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([lang, count]) => ({ lang, count }));
  };

  const ragaFolders = ragaSearchData?.groups || [];
  const ragaFolderOptions = ragaFolders.map(group => group.folder);
  const ragaLanguageOptions = [...new Set(ragaFolders
    .filter(group => ragaFolderFilter === 'ALL' || group.folder === ragaFolderFilter)
    .flatMap(group => Object.keys(group.languages)))].sort();
  const visibleRagaGroups = ragaFolders
    .filter(group => ragaFolderFilter === 'ALL' || group.folder === ragaFolderFilter)
    .map(group => ({
      ...group,
      languages: Object.fromEntries(Object.entries(group.languages)
        .filter(([language]) => ragaLanguageFilter === 'ALL' || language === ragaLanguageFilter))
    }))
    .filter(group => Object.keys(group.languages).length > 0);
  const visibleRagaLanguageCounts = visibleRagaGroups.reduce((counts, group) => {
    Object.entries(group.languages).forEach(([language, data]) => {
      counts[language] = (counts[language] || 0) + data.files.length;
    });
    return counts;
  }, {});

  if (loading) {
    return (
      <div className="np-loading-wrap">
        <Loader size="large" />
        <p className="np-loading-text">🪷 Loading sacred songs…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="np-error-box" style={{ margin: '2rem auto', maxWidth: 500 }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🙏</div>
        <p>{error}</p>
      </div>
    );
  }

  let filteredFiles = voiceSearchMatches !== null
    ? voiceSearchMatches.filter(f => {
      const displayName = formatFileName(f.file_name || f.name || '');
      return fileSearch ? displayName.toLowerCase().includes(fileSearch.toLowerCase()) : true;
    })
    : songSearch.trim()
      ? searchSongFiles().filter(f => {
        const displayName = formatFileName(f.file_name || f.name || '');
        return fileSearch ? displayName.toLowerCase().includes(fileSearch.toLowerCase()) : true;
      })
      : getFilteredFiles().filter(f => {
        const displayName = formatFileName(f.file_name);
        return displayName.toLowerCase().includes(fileSearch.toLowerCase());
      });

  if (sortBySongNumber) {
    filteredFiles = [...filteredFiles].sort((a, b) => {
      const songNoA = a.song_no != null && !isNaN(parseInt(a.song_no)) ? parseInt(a.song_no) : Number.MAX_SAFE_INTEGER;
      const songNoB = b.song_no != null && !isNaN(parseInt(b.song_no)) ? parseInt(b.song_no) : Number.MAX_SAFE_INTEGER;
      return sortDirection === 'asc' ? songNoA - songNoB : songNoB - songNoA;
    });
  }

  return (
    <div>
      {/* ── Top toolbar: playlist menu ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem', position: 'relative' }}>
        <button
          onClick={togglePlaylistMenu}
          className="np-btn np-btn-ghost"
          style={{ gap: '0.5rem', padding: '0.5rem 0.9rem' }}
          aria-label="Show playlists"
        >
          <FaList style={{ color: 'var(--gold-mid)' }} />
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '0.75rem', letterSpacing: '1px' }}>Playlists</span>
        </button>

        {playlistMenuOpen && (
          <div className="np-popover" style={{ position: 'absolute', right: 0, top: '110%', zIndex: 200, minWidth: 260, maxHeight: 400, overflowY: 'auto' }}>
            <div className="np-popover-header">🎵 Playlists</div>
            {playlists.length > 0 ? (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {playlists.map(playlist => (
                  <li
                    key={playlist.id}
                    className="np-popover-item"
                    onClick={() => handlePlaylistSelect(playlist)}
                  >
                    <div style={{ fontFamily: 'var(--font-body)', fontWeight: 600 }}>{playlist.name}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>ID: {playlist.id}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', fontFamily: 'var(--font-body)' }}>
                No playlists available
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Primary search controls ── */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
      {/* ── Song Search Panel ── */}
      <div className="np-panel np-panel-blue" style={{ flex: '1 1 320px', marginBottom: 0 }}>
        <label className="np-label" htmlFor="songSearch">
          <FaMusic style={{ color: 'var(--saffron)' }} />
          Search by Song Number or Name
        </label>
        <div style={{ position: 'relative' }}>
          <input
            id="songSearch"
            type="text"
            value={songSearch}
            onChange={(e) => { setVoiceSearchMatches(null); applySongSearch(e.target.value); }}
            placeholder="Type song number or name…"
            className="np-input"
            style={{ paddingRight: songSearch ? '2.8rem' : undefined }}
          />
          {songSearch && (
            <button
              onClick={() => { setSongSearch(''); setVoiceSearchMatches(null); }}
              style={{
                position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(184,134,11,0.12)', border: 'none', borderRadius: '50%',
                width: '1.9rem', height: '1.9rem', display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: 'pointer', color: 'var(--gold-deep)'
              }}
              aria-label="Clear search"
            >
              <FaTimes style={{ fontSize: '0.85rem' }} />
            </button>
          )}
        </div>
        {/* Voice Search is intentionally hidden for the initial public release. */}
      </div>

      {/* ── Raga Search Panel ── */}
      <div className="np-panel" style={{ flex: '1 1 320px', marginBottom: 0, borderColor: 'var(--gold-mid)' }}>
        <label className="np-label" htmlFor="ragaSearch">
          <FaMusic style={{ color: 'var(--saffron)' }} />
          Search by Raga
        </label>
        <input
          id="ragaSearch"
          type="text"
          value={ragaQuery}
          onChange={(e) => setRagaQuery(e.target.value)}
          placeholder="Type at least four letters, e.g. Mohanam…"
          className="np-input"
          aria-label="Search by raga"
        />
        {ragaSearchLoading && (
          <div style={{ marginTop: '0.55rem', fontSize: '0.77rem', color: 'var(--text-muted)' }}>Searching raga…</div>
        )}

        {ragaSearchError && <p className="np-error-box" style={{ margin: '0.8rem 0 0', padding: '0.6rem' }}>{ragaSearchError}</p>}

        {ragaSearchData?.suggestions?.length > 0 && (
          <div style={{ marginTop: '0.8rem' }}>
            <strong style={{ fontSize: '0.82rem' }}>Similar raga names:</strong>
            <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
              {ragaSearchData.suggestions.map(suggestion => (
                <button key={suggestion.raga_key} type="button" className="np-btn np-btn-ghost"
                  onClick={() => setRagaQuery(suggestion.raga_key)}>
                  {suggestion.display_name}
                </button>
              ))}
            </div>
          </div>
        )}

      </div>
      </div>

      {ragaSearchData?.resolved_raga?.raga_key && ragaSearchData.total_results > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
          <button
            type="button"
            className="np-btn np-btn-ghost"
            onClick={() => setMainResultsCollapsed(value => !value)}
            style={{ fontSize: '0.78rem' }}
          >
            {mainResultsCollapsed ? 'Show normal song results' : 'Hide normal song results'}
          </button>
        </div>
      )}

      {!mainResultsCollapsed && (
        <>
      {/* ── Folder + Language row ── */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>

        {/* Folder panel */}
        <div className="np-panel" style={{ flex: '1', minWidth: 240 }}>
          <label className="np-label" htmlFor="folderSearch">
            📁 Collection
          </label>
          <input
            id="folderSearch"
            type="text"
            value={folderSearch}
            onChange={(e) => {
              const v = e.target.value;
              setFolderSearch(v);
              if (v) {
                const matched = folders.filter(f => f.toLowerCase().replace(/_/g, ' ').includes(v.toLowerCase()));
                if (matched.length > 0) setSelectedFolder(matched[0]);
              }
            }}
            placeholder="Search collections…"
            className="np-input"
            style={{ marginBottom: '0.6rem' }}
          />
          <select value={selectedFolder} onChange={handleFolderChange} className="np-select">
            {filteredFolders.map(folder => (
              <option key={folder} value={folder}>{formatFolderName(folder)}</option>
            ))}
          </select>
        </div>

        {/* Language panel */}
        <div className="np-panel" style={{ flex: '1', minWidth: 240 }}>
          {/* Current language status */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            marginBottom: '0.75rem', padding: '0.35rem 0.75rem',
            background: 'linear-gradient(135deg, var(--maroon) 0%, var(--brown-mid) 100%)',
            borderRadius: 'var(--radius-pill)', width: 'fit-content'
          }}>
            <FaLanguage style={{ color: 'var(--gold-bright)', fontSize: '0.9rem' }} />
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '1px', color: 'var(--gold-light)' }}>
              {languageCodeMap[selectedLanguage] || selectedLanguage}
            </span>
          </div>

          {/* Language accordion */}
          <div className="np-accordion-header" onClick={() => setLanguageAccordionOpen(!languageAccordionOpen)}>
            <span className="np-label" style={{ margin: 0 }}>
              🔤 Language
            </span>
            <span className={`np-accordion-chevron ${languageAccordionOpen ? 'open' : ''}`}>▼</span>
          </div>

          {languageAccordionOpen && (
            <div style={{ marginTop: '0.65rem' }}>
              <input
                type="text"
                value={languageSearch}
                onChange={(e) => setLanguageSearch(e.target.value)}
                placeholder="Search languages…"
                disabled={!selectedFolder}
                className="np-input"
                style={{ marginBottom: '0.6rem', opacity: !selectedFolder ? 0.5 : 1 }}
              />
              <select
                value={selectedLanguage}
                onChange={handleLanguageChange}
                disabled={!selectedFolder}
                className="np-select"
                style={{ opacity: !selectedFolder ? 0.5 : 1, cursor: !selectedFolder ? 'not-allowed' : 'pointer' }}
              >
                {filteredLanguages.map(l => (
                  <option key={l} value={l}>{getLanguageDisplay(l)}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* ── Search Files accordion ── */}
      <div className="np-panel" style={{ marginBottom: '1.25rem' }}>
        <div className="np-accordion-header" onClick={() => setSearchAccordionOpen(!searchAccordionOpen)}>
          <span className="np-label" style={{ margin: 0 }}>
            <FaSearch style={{ color: 'var(--saffron)' }} /> Search within results
          </span>
          <span className={`np-accordion-chevron ${searchAccordionOpen ? 'open' : ''}`}>▼</span>
        </div>
        {searchAccordionOpen && (
          <input
            type="text"
            value={fileSearch}
            onChange={(e) => setFileSearch(e.target.value)}
            placeholder="Filter by file name…"
            className="np-input"
            style={{ marginTop: '0.65rem' }}
          />
        )}
      </div>

      {/* ── Sort + language count row ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>

        {/* Language song counts */}
        {getLanguageSongCounts().length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '0.7rem', fontWeight: 700, color: 'var(--brown-mid)', letterSpacing: '1px', textTransform: 'uppercase' }}>
              Songs:
            </span>
            {getLanguageSongCounts().map(({ lang, count }) => (
              <span key={lang} className={`np-pill ${lang === selectedLanguage ? 'np-pill-active' : 'np-pill-inactive'}`}>
                {count} {languageCodeMap[lang] || lang}
              </span>
            ))}
          </div>
        )}

        {/* Sort button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <button
            onClick={() => {
              if (sortBySongNumber) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
              else setSortBySongNumber(true);
            }}
            className={`np-btn np-btn-ghost ${sortBySongNumber ? 'np-btn-ghost-active' : ''}`}
            style={{
              fontSize: '0.75rem', padding: '0.4rem 0.8rem', gap: '0.4rem',
              ...(sortBySongNumber ? { borderColor: 'var(--gold-mid)', background: 'var(--gold-glow)', color: 'var(--brown-deep)' } : {})
            }}
          >
            {sortDirection === 'asc'
              ? <FaSortAmountDown style={{ fontSize: '0.85rem' }} />
              : <FaSortAmountUp style={{ fontSize: '0.85rem' }} />}
            Sort by #
            {sortBySongNumber && (
              <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>({sortDirection === 'asc' ? '↑' : '↓'})</span>
            )}
          </button>
          {sortBySongNumber && (
            <button
              onClick={() => setSortBySongNumber(false)}
              className="np-btn np-btn-ghost"
              style={{ padding: '0.4rem 0.6rem', fontSize: '0.75rem' }}
              title="Clear sort"
            >
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      {/* ── Song Tiles Grid ── */}
      {filteredFiles.length > 0 ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
          gap: '0.6rem'
        }}>
          {filteredFiles.map(file => {
            const fileName = file.file_name || file.name || '';
            const displayName = formatFileName(fileName);
            const songNo = file.song_no;

            return (
              <Link
                key={file.file_path || `${file.folder}-${file.language}-${fileName}`}
                to={`/file/${file.folder}/${file.language}/${encodeURIComponent(fileName)}`}
                className="np-song-card"
                onClick={() => window.clickLog('file_click - ' + file.folder + '/' + file.language + '/' + fileName, 'homepage')}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', paddingLeft: '0.4rem' }}>
                  <FaMusic style={{ color: 'var(--gold-mid)', fontSize: '0.75rem', marginTop: '0.15rem', flexShrink: 0 }} />
                  <div style={{ overflow: 'hidden', flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', marginBottom: '2px' }}>
                      <span className="np-song-badge">#{songNo || '—'}</span>
                      <span className="np-lang-tag">({languageCodeMap[file.language] || file.language})</span>
                    </div>
                    <p className="np-song-card-name">
                      {(songSearch && (() => {
                        const term = songSearch.toLowerCase();
                        const songNoStr = songNo ? songNo.toString() : '';
                        const songNoMatch = songNoStr === term || songNoStr.startsWith(term);
                        const fileNameMatch = displayName.toLowerCase().includes(term);
                        const isNumericSearch = /^\d+$/.test(term);
                        const fileNamePrefix = fileName ? fileName.match(/^(\d+)/)?.[1] : '';
                        const prefixMatch = isNumericSearch && fileNamePrefix && (fileNamePrefix === term || fileNamePrefix.startsWith(term));
                        return songNoMatch || fileNameMatch || prefixMatch;
                      })())
                        ? highlightMatchedText(displayName, songSearch)
                        : (fileSearch && displayName.toLowerCase().includes(fileSearch.toLowerCase()))
                          ? highlightMatchedText(displayName, fileSearch)
                          : displayName
                      }
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div style={{
          padding: '3rem 1rem', textAlign: 'center',
          background: 'var(--parchment)', borderRadius: 'var(--radius-md)',
          border: '1px dashed var(--border-gold)'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🪷</div>
          <p style={{ fontFamily: 'var(--font-body)', fontStyle: 'italic', color: 'var(--text-muted)' }}>
            {songSearch
              ? `No songs matching "${songSearch}" found.`
              : fileSearch
                ? `No files matching "${fileSearch}" found in ${formatFolderName(selectedFolder)} / ${selectedLanguage}.`
                : 'No files found for the selected collection and language.'}
          </p>
        </div>
      )}

        </>
      )}

      {/* ── Raga results: after the normal song-search results ── */}
      {ragaSearchData && ragaSearchData.resolved_raga?.raga_key && (
        <section className="np-panel" style={{ marginTop: '1.25rem', borderColor: 'var(--gold-mid)' }}>
          <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.8rem' }}>
            <strong style={{ color: 'var(--maroon-mid)' }}>
              Raga results: {ragaSearchData.resolved_raga.display_name || ragaSearchData.resolved_raga.raga_key}
            </strong>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {ragaSearchData.resolved_raga.raga_match_type.replace(/_/g, ' ')} match
            </span>
            {Object.entries(visibleRagaLanguageCounts).map(([language, count]) => (
              <span key={language} className="np-lang-tag">Songs: {count} {languageCodeMap[language] || language}</span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <select value={ragaFolderFilter} className="np-select" style={{ flex: '1', minWidth: 210 }}
              onChange={(e) => { setRagaFolderFilter(e.target.value); setRagaLanguageFilter('ALL'); }}>
              <option value="ALL">All collections</option>
              {ragaFolderOptions.map(folder => <option key={folder} value={folder}>{formatFolderName(folder)}</option>)}
            </select>
            <select value={ragaLanguageFilter} className="np-select" style={{ flex: '1', minWidth: 180 }} onChange={(e) => setRagaLanguageFilter(e.target.value)}>
              <option value="ALL">All languages</option>
              {ragaLanguageOptions.map(language => <option key={language} value={language}>{getLanguageDisplay(language)}</option>)}
            </select>
          </div>
          {visibleRagaGroups.map(group => (
            <section key={group.folder} style={{ marginTop: '0.9rem', borderTop: '1px solid var(--border-light)', paddingTop: '0.8rem' }}>
              <h3 style={{ margin: '0 0 0.55rem', fontFamily: 'var(--font-heading)', color: 'var(--brown-mid)', fontSize: '1rem' }}>{formatFolderName(group.folder)}</h3>
              {Object.entries(group.languages).map(([language, data]) => (
                <div key={language} style={{ marginBottom: '0.9rem' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '0.4rem' }}>
                    Songs: {data.files.length} {languageCodeMap[language] || language}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '0.6rem' }}>
                    {data.files.map(file => {
                      const fileName = file.name || file.path?.split('/').pop() || file.song_name;
                      return (
                        <Link key={file.song_id} to={`/file/${group.folder}/${language}/${encodeURIComponent(fileName)}`} className="np-song-card">
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', paddingLeft: '0.4rem' }}>
                            <FaMusic style={{ color: 'var(--gold-mid)', fontSize: '0.75rem', marginTop: '0.15rem', flexShrink: 0 }} />
                            <div style={{ overflow: 'hidden', flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', marginBottom: '2px' }}>
                                <span className="np-song-badge">#{file.song_no ?? '—'}</span>
                                <span className="np-lang-tag">({languageCodeMap[language] || language})</span>
                              </div>
                              <p className="np-song-card-name">{file.song_name || formatFileName(fileName)}</p>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>
          ))}
          {visibleRagaGroups.length === 0 && <p style={{ color: 'var(--text-muted)' }}>No songs match the selected collection and language filters.</p>}
        </section>
      )}

      {/* Scroll to top */}
      {showScrollTop && (
        <button className="np-scroll-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Scroll to top">
          <FaArrowUp />
        </button>
      )}
    </div>
  );
}

export default FileList;
